import { useState, useEffect, useRef, forwardRef } from "react";
import styles from "./MicroModalSelector.module.scss";
import { ArrowDown } from "./assets/icon";
import Loader from "../../molecule/Loader/Loader";

interface ResultItem {
  id: string;
  name: string;
  item: any;
}
export interface MicroModalSelectorSearchProps {
  valueSelectedName: string | null | undefined;
  placeholder?: string;
  setQuery: (query: string) => void;
  valueQuery?: string | null;
  result: ResultItem[];
  variant?: "dark" | "light";
  onClickItem: (item: any) => void;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  label?: string;
  
}

export default function MicroModalSelectorSearch({
  valueSelectedName,
  placeholder,
  result,
  setQuery,
  variant = "light",
  onClickItem,
  isLoading,
  valueQuery,
  label,
  isError,
}: MicroModalSelectorSearchProps) {
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showModal &&
        modalRef.current &&
        containerRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showModal]);

  return (
    <>
      <div
        ref={containerRef}
        className={[
          styles["global-container"],
          styles["global-container--" + variant],
        ].join(" ")}
      >
        <label
          className={[styles["label"], styles["label--" + variant]].join(" ")}
        >
          {label}
        </label>

        <div
          onClick={() => setShowModal(!showModal)}
          className={[
            styles["placeholder-container"],
            styles["placeholder-container--" + variant],
            styles[
              "placeholder-container--" +
                variant +
                "--" +
                (isError ? "error" : "")
            ],
          ].join(" ")}
        >
          <p
            className={[
              styles["placeholder-container__label"],
              styles["placeholder-container__label--" + variant],
            ].join(" ")}
          >
            {valueSelectedName || placeholder}
          </p>
          <div
            className={[
              styles["placeholder-container__icon"],
              styles["placeholder-container__icon--" + variant],
              showModal ? styles["placeholder-container__icon--active"] : "",
            ].join(" ")}
          >
            <ArrowDown
              size={15}
              color={variant === "dark" ? "white" : undefined}
            />
          </div>
        </div>
        {showModal && (
          <Modal
            ref={modalRef}
            result={result}
            onClickItem={onClickItem}
            variant={variant}
            setQuery={setQuery}
            valueQuery={valueQuery}
            handleCloseModal={() => setShowModal(false)}
            isLoading={isLoading}
          />
        )}
      </div>
    </>
  );
}

const Modal = forwardRef<HTMLDivElement, {
  result: ResultItem[];
  valueQuery?: string | null;
  setQuery: (s: string) => void;
  onClickItem: (item: any) => void;
  variant: "dark" | "light";
  isLoading?: boolean;
  handleCloseModal?: () => void;
}>(({
  result,
  onClickItem,
  variant,
  setQuery,
  valueQuery,
  isLoading,
  handleCloseModal,
}, ref) => {
  return (
    <div
      ref={ref}
      className={[
        styles["modal-container"],
        styles["modal-container--" + variant],
      ].join(" ")}
    >
      <div
        className={[
          styles["modal-container__search"],
          styles["modal-container__search--" + variant],
        ].join(" ")}
      >
        <input
          className={[
            styles["modal-container__search__input"],
            styles["modal-container__search__input--" + variant],
          ].join(" ")}
          type="text"
          placeholder="Rechercher"
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          value={valueQuery || ""}
        />
        <div className={[styles["border"], styles["border--"+variant]].join(" ")} />
      </div>
      <div
        className={[
          styles["modal-container__result"],
          styles["modal-container__result--" + variant],
        ].join(" ")}
      >
        {isLoading && (
          <Loader centered color={variant === "dark" ? "#FFF" : ""} />
        )}
        {!isLoading &&
          result &&
          result.length !== 0 &&
          result.map((item) => (
            <div
              key={item.id}
              className={[
                styles["modal-item"],
                styles["modal-item--" + variant],
              ].join(" ")}
              onClick={() => {
                onClickItem(item);
                handleCloseModal && handleCloseModal();
              }}
            >
              <p
                className={[
                  styles["modal-item__name"],
                  styles["modal-item__name--" + variant],
                ].join(" ")}
              >
                {item.name}
              </p>
            </div>
          ))}
        {!isLoading && result && result.length === 0 && (
          <p
            className={[
              styles["modal-container__empty"],
              styles["modal-container__empty--" + variant],
            ].join(" ")}
          >
            Aucun résultat
          </p>
        )}
      </div>
    </div>
  );
});
